import config from "../../../../config/config.json";
import { apiCall } from "../../../../_services/apiCall";

/**
 * All data-analytics endpoint calls as named functions. Components never
 * assemble URLs. Every call suppresses apiCall's global alert (6th arg) so the
 * per-source analytics loop can't spam popups — callers surface errors via
 * their own `error` state instead.
 */

const BASE = config.POSTLOGIN_API_URL_COMPANY;

/**
 * Quantitative reporting questions for a year, used only to resolve display
 * titles for the questionIds we already know. Deliberately NOT scoped by
 * framework: the caller filters by an explicit questionId list, and narrowing
 * by framework drops derived KPIs (which may be mapped to a different / no
 * framework).
 * @returns {Promise<Array>} the `data.data` question array (empty on failure).
 */
export const getReportingQuestions = async (
  financialYearId,
  questionTypes = ["quantitative"]
) => {
  const { isSuccess, data } = await apiCall(
    `${BASE}getReportingQuestionnew`,
    {},
    {
      financialYearId,
      question_type_filter: JSON.stringify(questionTypes),
    },
    "GET",
    true,
    false
  );
  return isSuccess && Array.isArray(data?.data) ? data.data : [];
};

/**
 * Optimized analytics for one set of locations. Pass `fromDate`/`toDate` to
 * scope to a month; omit them for the whole FY. Returns the raw analytics rows
 * (`{ qid, unit, selected_date_range_value, ... }`).
 * @returns {Promise<Array>}
 */
export const getOptimizedAnalytics = async ({
  financialYearId,
  previousFinancialYearId,
  locationIds,
  fromDate,
  toDate,
}) => {
  const { isSuccess, data } = await apiCall(
    `${BASE}quantative-data/optimized-analytics`,
    {},
    {
      financialYearId,
      previousFinancialYearId,
      locationIds,
      ...(fromDate ? { fromDate } : {}),
      ...(toDate ? { toDate } : {}),
    },
    "GET",
    true,
    false
  );
  if (!isSuccess) return [];
  return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
};

/**
 * Optimized analytics broken down by location — one row per (location,
 * question), each tagged with `locationId`. Returns the same value fields as
 * getOptimizedAnalytics plus `locationId`, so the whole location set is fetched
 * in a single call instead of one-per-source.
 * @returns {Promise<Array>}
 */
export const getOptimizedAnalyticsByLocation = async ({
  financialYearId,
  previousFinancialYearId,
  locationIds,
  fromDate,
  toDate,
  questionIds,
}) => {
  const { isSuccess, data } = await apiCall(
    `${BASE}quantative-data/optimized-analytics-by-location`,
    {},
    {
      financialYearId,
      previousFinancialYearId,
      locationIds,
      ...(questionIds ? { questionIds } : {}),
      ...(fromDate ? { fromDate } : {}),
      ...(toDate ? { toDate } : {}),
    },
    "GET",
    true,
    false
  );
  if (!isSuccess) return [];
  return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
};

/**
 * Trigger rows (min/max targets per question, source and period) for a FY.
 * @returns {Promise<Array>} the `data.data` trigger array (empty on failure).
 */
export const getTriggers = async (financialYearId) => {
  const { isSuccess, data } = await apiCall(
    `${BASE}triggers/${financialYearId}`,
    {},
    {},
    "GET",
    true,
    false
  );
  return isSuccess && Array.isArray(data?.data) ? data.data : [];
};
